import { useState, useEffect, useRef } from "react"
import PubSub from "pubsub-js"
import { api } from "aldoo-ra/API"
import Typography from "aldoo-ra/Typography"
import Spinner from "aldoo-ra/Spinner"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import { getStoreAccount } from "aldoo-ra/Store/api"
import BillingToggle from "./BillingToggle"
import OneTimeCard, { getLifetimeTiers } from "./OneTimeCard"
import ProCard from "./ProCard"
import { fetchPlanFeatures } from "./plan-features"
import MainBackground from "@components/MainBackground"

const snapAPI = api("snap")

/**
 * Pricing page — two cards:
 *  - One-Time: lifetime license priced for a single Mac; Continue opens the
 *    seat-pack picker (1/2/5/10 Macs) and hands off to the store checkout.
 *  - Pro: per-seat subscription, monthly/annual via the billing toggle.
 */
export default function Pricing() {
  const texts = TextBundle({ id: "pricing" })
  const [billing, setBilling] = useState("annual")
  const [storeData, setStoreData] = useState(null)
  const [activePid, setActivePid] = useState(null)
  const [featuresByPid, setFeaturesByPid] = useState({})

  useEffect(() => {
    fetchPlanFeatures().then(setFeaturesByPid).catch(() => {})
    snapAPI({ pricing: true })
      .then((data) => {
        setStoreData({ products: data?.products || {}, prices: data?.prices || {} })
      })
      .catch(() => setStoreData({ products: {}, prices: {} }))

    getStoreAccount()
      .then((res) => {
        const account = res?.result || res
        const subs = account?.subscriptions || []
        const active = subs.find((s) => s.pid?.startsWith("snap-pro-") && s.status === "active")
        if (active) setActivePid(active.pid)
      })
      .catch(() => {})
  }, [])

  const loading = storeData === null

  // GA4 view_item_list — fire once when store prices have resolved so items
  // carry real price + currency. Without it `add_to_cart` looks like the
  // funnel entry point and drop-off attribution is skewed.
  const viewItemListFiredRef = useRef(false)
  useEffect(() => {
    if (viewItemListFiredRef.current || loading) return

    const items = []
    let currency = "USD"

    const baseTier = getLifetimeTiers(storeData)[0]
    if (baseTier) {
      currency = baseTier.model.currency || currency
      items.push({
        item_id: baseTier.product.pid,
        item_name: baseTier.product.name,
        price: baseTier.amount,
        quantity: 1,
      })
    }

    const proPid = billing === "annual" ? "snap-pro-annual" : "snap-pro-monthly"
    const proProduct = Object.values(storeData.products).find((p) => p.pid === proPid)
    const proPrice = proProduct && storeData.prices[String(proProduct.price?._id || proProduct.price)]
    const proModel = proPrice?.price_model?.find((m) => m.primary) || proPrice?.price_model?.[0]
    if (proProduct && proModel) {
      currency = proModel.currency || currency
      items.push({
        item_id: proProduct.pid,
        item_name: proProduct.name,
        price: proModel.amount,
        quantity: 1,
      })
    }

    if (!items.length) return
    PubSub.publish("Tracking_ViewItemList", {
      items,
      currency,
      itemListName: "Pricing",
    })
    viewItemListFiredRef.current = true
  }, [loading, storeData, billing])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen pt-20 bg-canvas">
        <Spinner variant="site-loading" />
      </div>
    )
  }

  return (
    <div className="min-h-screen">
      <div className="fixed inset-0 pointer-events-none">
        <MainBackground />
      </div>
      <div className="relative -mt-16  overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 md:pt-40 pb-10 sm:pb-16 md:pb-28">
          {/* Header */}
          <div className="text-center mb-8">
            <Typography variant="h1" className="text-text mb-6">
              {texts?.page_title}
            </Typography>

            <Typography variant="body1" className="max-w-2xl lg:max-w-3xl mx-auto">
              {texts?.page_subtitle}
            </Typography>
          </div>

          <BillingToggle
            billing={billing}
            setBilling={setBilling}
            texts={texts}
          />

          <div id="plans" className="w-full max-w-[340px] md:max-w-[720px] lg:max-w-[800px] grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 mx-auto">
            {/* Mobile order: Pro on top, One-Time below. Desktop keeps the
                grid's natural left-to-right order (One-Time, Pro). */}
            <div className="order-2 md:order-none">
              <OneTimeCard 
                storeData={storeData} 
                texts={texts} 
                featuresByPid={featuresByPid} 
              />
            </div>
            <div className="order-1 md:order-none">
              <ProCard
                storeData={storeData}
                billing={billing}
                activePid={activePid}
                featuresByPid={featuresByPid}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
